import area from '@turf/area'
import bboxPolygon from '@turf/bbox-polygon'
import { feature, featureCollection } from '@turf/helpers'
import $ from 'jquery'
import { isEmpty } from 'lodash'
import filter from 'lodash/filter'
import get from 'lodash/get'
import has from 'lodash/has'
import includes from 'lodash/includes'
import isArray from 'lodash/isArray'
import isFunction from 'lodash/isFunction'
import isNil from 'lodash/isNil'
import isNumber from 'lodash/isNumber'
import isString from 'lodash/isString'
import snakeCase from 'lodash/snakeCase'
import toLower from 'lodash/toLower'
import OLFormatGeoJSON from 'ol/format/GeoJSON'
import OLLayerGroup from 'ol/layer/Group'
import OLRenderFeature from 'ol/render/Feature'
import OLSourceImageWMS from 'ol/source/ImageWMS'
import OLSourceTileWMS from 'ol/source/TileWMS'
import OLSourceVector from 'ol/source/Vector'
import { reduxStore } from 'reduxStore'

import type { BBox, FeatureCollection } from '@turf/helpers'
import type OLCollection from 'ol/Collection'
import type OLFeature from 'ol/Feature'
import type OLGeomGeometry from 'ol/geom/Geometry'
import type OLLayerBase from 'ol/layer/Base'
import type OLLayer from 'ol/layer/Layer'
import type { ProjectionLike } from 'ol/proj'
import type OLSource from 'ol/source/Source'
import type OLSourceXYZ from 'ol/source/XYZ'

import * as Model from 'api/model'
import type { ConfigLayerCapabilityOptions } from 'config/catalog/ConfigLayerCapability'
import { ConfigLayerQGISProjectSettings } from 'config/catalog/ConfigLayerQGISProjectSettings'
import type { ConfigLayerOptions } from 'config/ConfigLayer'
import { ConfigLayerType } from 'config/ConfigLayerType'
import GetPropertiesIdKey from 'data/GetPropertiesIdKey'
import { SET_LAYER_LOAD_FAILED, setLayerHidden, setLayerSelected, setLayerUnselected, setLayerVisible } from 'drawer/tabs/layers/actions'
import IsExclusiveGroup from 'drawer/tabs/layers/utils/IsExclusiveGroup'
import highlightVectorFeature from 'map/HighlightVectorFeature'
import selectVectorFeature from 'map/SelectVectorFeature'
import { getMap } from 'utils/MapUtils'
import PoziNotifications from 'utils/notifications'
import { equalsIgnoreCase } from 'utils/text'

/* PoziStats is a custom storage format */
export interface PoziStats {
  config?: ConfigLayerOptions | null
  count?: number
  features?: Record<string, any>[]
  properties?: Record<string, any>
  title?: string
  titleid?: string
}

/**
 * Utils
 */

// Convenient way to get access to the app object
export const getApp = () => window.appObject

// Used to exclude properties typically used programatically. E.g. ID, marker-color, etc.
// Edit templates.json->defaultconfig->settings->excludeProperties
export const removeExcludedPropertiesFromArray = function (arr: [number | string]) {
  const excludeProperties = Model.get('config.settings.excludeProperties', []) // This is set in templates.json
  return filter(arr, function (v) {
    if (includes(excludeProperties, v.toString().toLowerCase())) {
      return false
    }
    return true
  })
}

export const timestampRoundedUpToMinutes = function (minutes: number) {
  const coeff = 1000 * 60 * minutes
  return new Date(Math.ceil(Date.now() / coeff) * coeff).getTime()
}

// Another option is to catch this in the custom loader.
// See /config/LoaderHandleGeoJSON.js for more details.
export const setSourceLoaded = function (olLayer: OLLayer, source: OLSource | OLSourceVector | OLSourceXYZ, layerConf: ConfigLayerOptions) {
  if (isNil(olLayer) || isNil(source)) {
    return null
  }

  if (!(source instanceof OLSourceVector)) {
    const type = get(layerConf, 'type', '')
    if (type === ConfigLayerType.GEOJSON) {
      console.warn('Cannot get features from GeoJSON source!!', layerConf.titleid)
    }
    return null
  }

  if (isNil(layerConf)) {
    return null
  }

  // Doesn't work. https://github.com/openlayers/openlayers/pull/8532
  /* olLayer.on('rendercomplete', function () {
    console.log('LAYER LOADED on rendercomplete?? Never triggered.')
  }) */

  // console.log('create on source change event', layer.title)
  source.once('change', function () {
    if (source.getState() === 'ready') {
      // console.log('source ready and changed', layer.title)
      if (!isFunction(source.getFeatures)) {
        console.warn('Source has no getFeatures function.... is it a vectortile source?')
        return null
      }

      const olFeaturesArr = source.getFeatures()
      // May be fixed?? https://github.com/openlayers/openlayers/pull/8532
      // console.log('geojsonSource first data element loaded... See openlayers issue 2320 and 8532. Features length', layer.titleid, olFeaturesArr.length)
      // HORRIBLE... how can we detect feature load completed.
      window.setTimeout(function () {
        // https://github.com/openlayers/openlayers/issues/2320
        if (!isFunction(source.getFeatures)) {
          console.warn('Cannot get features from source even after onready and timeout??')
          return null
        }
        const olFeaturesArr = source.getFeatures()
        // May be fixed?? https://github.com/openlayers/openlayers/pull/8532
        // console.log('Hopefully all source elements are now loaded... See openlayers issue 2320 and 8532. Feature length', layer.titleid, olFeaturesArr.length)
        if (get(layerConf, 'titleid', null)) {
          Model.set('app.config.layerbytitleid[' + layerConf.titleid + '].loaded', true)
          Model.set('app.config.layerbytitleid[' + layerConf.titleid + '].visible', true)
          const data = convertOLFeatureArrToTurfFeature(olFeaturesArr)
          Model.set('app.config.layerbytitleid[' + layerConf.titleid + '].data', data)
        } else {
          console.error('setSourceLoaded: Unable to load layer title.')
        }
      }, 100)
    }
  })
}

/**
 * Searches an OpenLayers layer for features that have a fieldName that matches any of the given fieldValues.
 * The search is case-insensitive.
 * It returns an array of OpenLayers features
 */
export const findFeaturesByFieldValues = (titleid: string, fieldValues: string[], fieldName?: string) => {
  console.debug('findFeaturesByFieldValues', titleid, fieldValues, fieldName)

  const layerConf: ConfigLayerOptions | null = getLayerFromTitleID(titleid)
  if (!layerConf) {
    console.log('findFeaturesByFieldValues - layerConf not found', titleid)
    return
  }

  // Try using the ID field
  if (!fieldName) {
    fieldName = layerConf.config?.spatial?.id
  }

  if (!fieldName) {
    return
  }

  const olLayer = getOpenLayersObjectFromTitleID(titleid)
  if (!olLayer) {
    return
  }

  const source = olLayer.getSource()
  if (!source) {
    return
  }

  let featureFieldValue = ''
  const olSourceState = source.getState()
  if (olSourceState !== 'ready') {
    console.log('findFeaturesByFieldValues - attempted to search layer that is currently not loaded', layerConf)
    return
  }

  if (!(source instanceof OLSourceVector)) {
    console.log('findFeaturesByFieldValues - source is not a vector source, so cannot be searched', source)
    return
  }

  // Convert field values to lowercase. This makes it easier to compare values
  const fieldValuesLC = fieldValues.map((value) => String(value).toLowerCase())

  const features: OLFeature[] = []

  const olFeaturesArr = source.getFeatures()
  for (const olFeature of olFeaturesArr) {
    featureFieldValue = getFeaturePropertyValueCaseInsensitive(olFeature, fieldName, '')
    const featureFieldValueLC = String(featureFieldValue).toLowerCase()

    if (fieldValuesLC.includes(featureFieldValueLC)) {
      console.debug('findFeaturesByFieldValues - found feature from text', fieldValues, fieldName, featureFieldValue)

      features.push(olFeature)
    }
  }

  console.debug('findFeaturesByFieldValues - found features', features)

  return features
}

/**
 * Select a feature in the current search layer based on the value of a given field (default is the ID field).
 */
export const selectFeatureInSearchLayer = function (fieldValue: string, fieldName?: string) {
  const app = getApp()
  let featureFound = false
  const searchLayerConf = Model.get('app.search.layer', null)
  if (!searchLayerConf) return

  const searchLayerIdField = searchLayerConf.config?.spatial?.id
  fieldName = fieldName || searchLayerIdField
  if (!fieldName) return

  const olLayer = getOpenLayersObjectFromTitleID(searchLayerConf.titleid)
  if (!olLayer) return

  const source = olLayer.getSource()
  if (!source) return

  fieldValue = String(fieldValue)
  Model.setState('feature', searchLayerConf.titleid, fieldValue)
  Model.set('app.search.suggestion', fieldValue)
  let featureFieldValue = ''
  const olSourceState = source.getState()
  if (olSourceState === 'ready') {
    if (!(source instanceof OLSourceVector)) {
      console.log('Source is not a vector source, so cannot be searched', source)
      return false
    }
    const olFeaturesArr = source.getFeatures()
    for (const key in olFeaturesArr) {
      const olFeature = olFeaturesArr[key]
      featureFieldValue = getFeaturePropertyValueCaseInsensitive(olFeature, fieldName, '') + ''
      if (featureFieldValue === fieldValue) {
        featureFound = true
        selectVectorFeature(olFeature, olLayer)
        console.log('Found feature from text', fieldValue, fieldName, searchLayerConf)
        break
      }
    }
  } else {
    console.error('Attempted to search layer that is currently not loaded', searchLayerConf)
  }

  if (!featureFound) {
    console.log('Unable to find feature from text', fieldValue, fieldName, searchLayerConf)
  }
  return featureFound
}

/**
 * Select a feature based on the text of the featureid.
 */
interface ExtraOptions {
  idField?: string | null
  setStyleOnly?: boolean
  zoomFunction?: () => any
}

export const selectVectorFeatureFromIDTextAndLayerConfig = function (idtext: string, titleid: string, extraOptions?: ExtraOptions) {
  if (!titleid) {
    return null
  }

  const layerConf = getLayerFromTitleID(titleid)
  const title = layerConf?.title

  if (!title) {
    return null
  }

  const { idField, setStyleOnly = false, zoomFunction = () => {} } = extraOptions || {}

  const app = getApp()
  const featureIDRef = idField || (app?.layerConfig?.[title]?.config?.config?.spatial?.id ?? null)
  if (isNil(featureIDRef)) return null

  const olLayer = getOpenLayersObjectFromTitleID(titleid)
  if (isNil(olLayer)) return null

  // Some OL layers do not have a source (i.e. LayerGroup layers)
  const source = olLayer.getSource ? olLayer.getSource() : null
  if (isNil(source)) return null

  idtext = String(idtext)
  let bFoundFeature = false
  let featureIDValue = ''
  const olSourceState = source.getState()
  let olFeature = null
  if (olSourceState === 'ready') {
    if (!(source instanceof OLSourceVector)) {
      return null
    }
    const olFeaturesArr: OLFeature[] = source.getFeatures()
    for (const key in olFeaturesArr) {
      olFeature = olFeaturesArr[key]
      featureIDValue = getFeaturePropertyValueCaseInsensitive(olFeature, featureIDRef)
      if (equalsIgnoreCase(featureIDValue, idtext)) {
        bFoundFeature = true
        if (setStyleOnly) {
          console.debug('selectVectorFeatureFromIDTextAndLayerConfig Set style only.', titleid, featureIDRef, featureIDValue)

          highlightVectorFeature(olFeature, olLayer)
        } else {
          console.debug('selectVectorFeatureFromIDTextAndLayerConfig Select vector feature.', titleid, featureIDRef, featureIDValue)

          selectVectorFeature(olFeature, olLayer)
        }
        break
      }
    }
  } else {
    console.error('Attempted to search unloaded source.', titleid)
    return null
  }

  if (!bFoundFeature) {
    console.log('Unable to find feature from text', idtext)
    return null
  }

  if (!setStyleOnly) {
    Model.setState('feature', titleid, idtext)
  }

  if (zoomFunction && olFeature && layerConf) {
    if (!isFeatureInExtent(olFeature, layerConf)) {
      zoomFunction()
    }
  }

  return olFeature
}

// Do case insensitive lookup for the correct prop name from a properties object
export const findPropName = (props: Record<string, any>, propNameCI: string) => {
  // console.debug('findPropName', propNameCI)

  if (!props || !propNameCI) {
    console.debug('findPropName - Either props or propName not provided:', props, propNameCI)
    return
  }

  const propName = Object.keys(props).find(key => key.toLowerCase() === propNameCI.toLowerCase())

  if (!propName) {
    // console.debug('findPropName - Could not find property in feature', propNameCI, props)
    return
  }

  return propName
}

// Do case insensitive lookup for the correct prop name from an OL feature
export const findFeaturePropName = (olFeature: OLFeature | OLRenderFeature, propNameCI: string) => {
  if (!olFeature || !propNameCI) {
    console.debug('findFeaturePropName - Either feature or propName not provided:', olFeature, propNameCI)
    return
  }

  const props = olFeature.getProperties() || {}

  return findPropName(props, propNameCI)
}

// Do a case insensitive lookup of a property value
export const getPropertyValueCaseInsensitive = (props: Record<string, any>, propNameCI: string, defaultValue: any = undefined) => {
  // console.debug('getPropertyValueCaseInsensitive', propNameCI, defaultValue)

  const propName = findPropName(props, propNameCI)

  if (!propName) {
    return defaultValue
  }

  return props[propName] ?? defaultValue
}

// Do a case insensitive lookup of a feature property value
export const getFeaturePropertyValueCaseInsensitive = (olFeature: OLFeature | OLRenderFeature, propNameCI: string, defaultValue: any = undefined) => {
  // console.debug('getFeaturePropertyValueCaseInsensitive', propNameCI, defaultValue)

  const props = olFeature.getProperties()

  return getPropertyValueCaseInsensitive(props, propNameCI, defaultValue)
}

/**
 * Get pozi server version
 */
export const getPoziServerVersion = function () {
  let poziserverVersion = Model.get('config.localDataSource.poziserver', '')
  poziserverVersion = replaceAll(poziserverVersion, '.', '')
  if (poziserverVersion === '') {
    poziserverVersion = 0
  }
  poziserverVersion = poziserverVersion * 1
  return poziserverVersion || 0
}

/**
 * Get layers array from groupID
 */
export const getLayersVisibleWithForm = function () {
  const layers = Model.get('config.layers', [])
  if (layers && layers.length > 0) {
    const layerbytitleid = Model.get('app.config.layerbytitleid', [])
    const layersWithForm = filter(layers, function (o) {
      return get(o, 'form', '') !== '' && get(layerbytitleid, '[' + o.titleid + '].visible', false)
    })
    if (!isNil(layersWithForm)) {
      return layersWithForm
    }
  }
  console.error('No groups found with a form')
  return null
}

/**
 * Get layers with search config
 */
export const getLayersSearchable = function () {
  const layerConfs = Model.get('config.layers', [])
  if (layerConfs && layerConfs.length > 0) {
    const layersWithSearch = filter(layerConfs, (layerConf) => {
      if (layerConf.enabled === false) {
        return false
      }

      const hasSearch = layerConf.search?.enabled || Array.isArray(layerConf.searchFields)
      if (!hasSearch) {
        return false
      }

      if (!!layerConf.localDataSource) {
        const isLocalDataSourceDetected = Model.get('app.LocalDataSourceDetected', false)
        // console.debug('Do not show private search layers when no local datasource found.', layer.titleid, layer)
        return isLocalDataSourceDetected
      }

      return true
    })
    if (layersWithSearch) {
      return layersWithSearch
    }
  }
  console.error('No layers with search found.')
  return []
}

/**
 * Show and hide an element with nice effects. Typically used for info panels.
 */
export const showElementThenHideAfterAFewSeconds = function (element: HTMLElement) {
  $(element).fadeIn()
  window.setTimeout(function () {
    $(element).fadeOut()
  }, 10000)
}

/**
 * Stops propogation or default actions from occuring.
 */
export const eventHandled = function (e: Event) {
  if (e) {
    e.stopPropagation() // Do not bubble. Cancels any click events of buttons, objects that might be overlapping.
    e.preventDefault() // Do not do the default action. E.g. Cancels following a link or submitting a form.
  }
}

/**
 * Convert OL feature to GeoJSON.
 */
export const convertOLFeatureToTurfFeature = function (olFeature: OLFeature) {
  // console.debug('convertOLFeatureToTurfFeature', olFeature)

  if (has(olFeature, 'flatCoordinates_')) {
    return convertRenderFeatureToTurfFeature(olFeature as unknown as OLRenderFeature)
  }

  try {
    const olFormatGeoJSON = new OLFormatGeoJSON()
    const geoJSON = olFormatGeoJSON.writeFeatureObject(olFeature as unknown as OLFeature)

    return geoJSON
  } catch (err) {
    console.error('Unable to parse feature!', err, olFeature)
  }
  return null
}

/**
 * Convert ol features array to turf features.
 */
export const convertOLFeatureArrToTurfFeature = function (olFeatureArr: OLFeature<OLGeomGeometry>[] | OLRenderFeature[], options = {}) {
  if (isNil(olFeatureArr) || olFeatureArr.length === 0) {
    return null
  }

  const firstFeature = olFeatureArr[0]
  // if (has(firstF, 'flatCoordinates_')) {
  if (firstFeature instanceof OLRenderFeature) {
    const fc = featureCollection([convertRenderFeatureToTurfFeature(firstFeature)])
    return fc
  }

  const olFormatGeoJSON = new OLFormatGeoJSON()
  // TODO: Add options and remove conversion from callees.
  // const options = {
  //   dataProjection: 'EPSG:4326',
  //   featureProjection: 'EPSG:3857'
  // }
  return olFormatGeoJSON.writeFeaturesObject(olFeatureArr as OLFeature[], options)
}

export const convertRenderFeatureToTurfFeature = function (renderFeature: OLRenderFeature) {
  const extents = renderFeature.getExtent()
  const polygon = bboxPolygon(extents as BBox)
  const properties = renderFeature.getProperties()
  return feature(polygon.geometry, properties)
}

/**
 * Convert GeoJSON FeatureCollection to OL feature array
 */
export const convertGeoJSONFeatureCollectionToOLFeatureArray = function (geoJSON: FeatureCollection) {
  try {
    const options = {
      defaultDataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857'
    }

    // Type casting to 'OLFeature<OLGeomGeometry>[]' won't be needed in the next version of
    // OpenLayers (either 8.2.x, 8.3.x or 9.0.x)
    return new OLFormatGeoJSON(options).readFeatures(geoJSON)
  } catch (e) {
    console.log(e)
  }
  return []
}

/**
 * Returns properties of json object
 */
export const getGeoJSONProperties = function (tabConfig: Record<string, any>, json: FeatureCollection) {
  let properties = get(json, 'properties', null)
  if (!properties) {
    properties = get(json, 'Location.Address', null) // Handle Heremaps json format.
  }
  if (!properties) {
    console.error('Unable to parse GeoJSON. Return whole object?')
    return json
  }
  return properties
}

export type GeomTypeSimple = 'geometrycollection' | 'line' | 'point' | 'polygon' | 'nogeometry'

// Simplify geometry types to either 'line', 'point', 'polygon' or 'nogeometry'.
export const getGeomTypeSimple = (geomType: string = ''): GeomTypeSimple => {
  const geomTypeMatches = [...geomType.matchAll(/(curve|geometrycollection|line|point|polygon|nogeometry)/ig)] // array from regexp string iterator
  const geomTypeSimple = (geomTypeMatches[0]?.[0] || '').toLowerCase() // take first match

  if (!geomTypeSimple || geomTypeSimple === 'nogeometry') {
    return 'nogeometry' as GeomTypeSimple
  }

  if (geomTypeSimple === 'curve') {
    return 'line' as GeomTypeSimple
  }

  return geomTypeSimple as GeomTypeSimple
}

/**
 * Returns features of json object
 */
// export const getGeoJSONFeatures = function (tabConfig, json: JSON) {
//   var features = get(json, 'features', null)
//   if (!features) {
//     features = get(json, 'Response.View.0.Result', null) // Handle Heremaps json format.
//   }
//   if (!features) {
//     console.error('Unable to parse GeoJSON. Return whole object?')
//     return json
//   }
//   return features
// }

/**
 * Generate a basic GUID
 */
export const getGUID = function () {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1)
  }
  return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4()
}

// Hmmm, WTF?
export const toUpperCasePozi = function (str: string) {
  return str.replace(/_/g, ' ').replace(/\w\S*/g, function (txt) {
    return txt.toUpperCase()
  })
}

/**
 * Returns the name of the first key in the array.
 * E.g. app.state.z[9]=='' returns 9
 *
 * This is a bit hacky, but allows us to represent single values in a seralisable way.
 * Take the following example:
 *   app.state.cen[lat]==aa, app.state.cen[lon]==bb
 * But if you just want a single value it is:
 *  app.state.z[9]==''
 * This function allows you to get the data which is the number 9
 * Alternatively we could have had app.state.z[0]==9. Toss of coin.
 *
 */
export const getNameOfFirstKey = function (arr: any) {
  if (!arr) {
    return null
  }
  return Object.keys(arr)[0]
}

/**
 * Sort FeatureCollection By Area
 * @param {featureCollection} Unsorted
 * @returns {featureCollection} Sorted largest to smallest
 */
export const sortFeatureCollectionByArea = function (featureCollection: FeatureCollection) {
  const features = get(featureCollection, 'features', [])
  if (features.length > 0) {
    console.log('No features in collection.')
    return featureCollection
  }

  for (const featureKey in features) {
    const feature = features[featureKey]
    if (!feature.properties) {
      feature.properties = {}
    }

    feature.properties.area = area(feature)
  }

  features.sort(function (a, b) {
    return a.properties?.area > b.properties?.area ? -1 : 1
  })

  return featureCollection
}

/**
 * Wrapper for Google Analytics for consistant message format.
 *
 * GA4: https://support.google.com/analytics/answer/11091025
 *
 * @param {Object} options
 * @typedef {{
 *   category: (string),      // config | program | data | useraction
 *   status: (string),        // success | warning | critical
 *   identifier: (string),    // Some identiable aspect of the category.
 *   action: (string),        // Action or task that occured.
 *   additionalInfo: (string) // Some additional inforamation.
 * }}
 * @example
 *  var options = {
 *    category: 'config',
 *    status: 'warning',
 *    identifier: 'hard waste',
 *    action: 'card open',
 *    additionalInfo: 'Failed to load CKANID 1234'
 *  }
 *  PoziUtils.gtag(options)
 */
export const gtag = function (options: Record<string, any>) {
  const category = options.category || ''
  const status = options.status || ''
  const identifier = options.identifier || ''
  const action = options.action || ''
  const additionalInfo = options.additionalInfo || ''

  const validCategories = ['config', 'data', 'global', 'program', 'useraction']
  if (!validCategories.includes(category)) {
    console.error('Invalid ga eventCategory:', category, 'ga options', options)

    // Analytics GA4
    window.gtag('event', 'ga_invalid_category', {
      category: 'program',
      identifier: category,
      status: 'warning'
    })
    return
  }
  const validStatus = ['success', 'warning', 'critical']
  if (!validStatus.includes(status)) {
    console.error('Invalid ga status:', status, 'ga options', options)

    // Analytics GA4
    window.gtag('event', 'ga_invalid_status', {
      category: 'program',
      identifier: status,
      status: 'warning'
    })

    return
  }

  // Analytics GA4
  const eventActionSnakeCase = snakeCase(options.action)
  const eventProps = {
    ...options
  }

  delete eventProps.action
  delete eventProps.additionalInfo

  if (additionalInfo) {
    eventProps.additional_info = additionalInfo
  }

  window.gtag('event', eventActionSnakeCase, eventProps)

  // console.debug('gtag event', eventActionSnakeCase, eventProps)
}

export const gtagInit = function () {
  // Retrieve relevant information from getAppInfo, which is defined in index.html
  const appInfo = window.getAppInfo()

  const site = Model.get('app.site', 'UNKNOWN')
  const title = Model.get('config.title', 'UNKNOWN')

  // Report when a local data source has been detected, i.e (part of) the access is restricted
  const isLocalDataSourceDetected = Model.get('app.LocalDataSourceDetected', false)

  // Set config for PoziApp-G4 (G-13RPCEPHCE)
  window.gtag('config', 'G-13RPCEPHCE', {
    ...appInfo, // get app info details

    // Keep track of public vs restricted access
    access: isLocalDataSourceDetected ? 'restricted' : 'public',

    // Only use site and title from config/model, to prevent noise in the
    // analytics (like random site ids or translated page titles)
    site,
    title,

    // debug_mode: true, // Set to true for showing up in debug view of GA4
    transport_type: 'beacon' // use navigator.sendBeacon if available
  })

  // Report when a local data source has been detected
  if (isLocalDataSourceDetected) {
    const PoziServerURL = Model.get('config.localDataSource.PoziServerURL', '')

    gtag({
      category: 'config',
      status: 'success',
      identifier: PoziServerURL,
      action: 'Local Datasource Detected'
    })
  }
}

// Returns a property in an object (might be ordered or not)
function keyInObject(obj: Record<string, any>) {
  for (const prop in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, prop)) continue
    return prop
  }
}

//
// Cookie helpers from http://www.quirksmode.org/js/cookies.html
//
export const createCookie = function (name: string, value: string, days = 0) {
  let expires
  if (days) {
    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    expires = '; expires=' + date.toUTCString()
  } else {
    expires = ';'
  }
  document.cookie = encodeURIComponent(name) + '=' + encodeURIComponent(value) + expires + ' path=/; SameSite=None; Secure'
}

export const readCookie = function (name: string) {
  const nameEQ = encodeURIComponent(name) + '='
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length))
    }
  }
  return null
}
export const eraseCookie = function (name: string) {
  createCookie(name, '', -1)
}

/**
 * Query the site config for a layer.
 *
 * https://lodash.com/docs/4.17.4#get
 * @param {string} title
 * @returns {object}
 *
 */
export const getLayerFromTitle = function (title: string) {
  if (isNil(title) || title === '') {
    return null
  }
  // var state = window.store.getState()
  // var reduxLayers = state.layers
  const layers = Model.get('config.layers')

  for (const key in layers) {
    if (toLower(layers[key].title) === toLower(title)) {
      return layers[key]
    }
  }
  console.error('Layer not found! ', title)
  return null
}

/**
 * Query the site config for a tab.
 *
 * https://lodash.com/docs/4.17.4#get
 * @param {string} title
 * @returns {object}
 *
 */
export const getTabFromTitle = function (title: string) {
  if (isNil(title) || title === '') {
    return null
  }
  const tabs = Model.get('config.modes[0].tabs', [])
  for (const key in tabs) {
    if (tabs[key].title === title) {
      return tabs[key]
    }
  }
  console.log('Tab not found! ', title)
  return null
}

/**
 * Test if titleid is a valid string.
 *
 * @param {string} titleid
 * @returns {bool}
 *
 */
export const isValidTitleID = function (titleid: string) {
  const bContainsSpaces = titleid.indexOf(' ') > -1
  if (!titleid || typeof titleid !== 'string' || titleid === '' || bContainsSpaces) {
    console.error('Invalid titleid.')
    return false
  }
  return true
}

/**
 * Query the site config for a layer.
 *
 * https://lodash.com/docs/4.17.4#get
 * @param {string} titleid // As retrieved from getPoziIDFromString
 * @returns {object}
 *
 */
export const getLayerFromTitleID = (titleid: string) => {
  if (isNil(titleid) || titleid === '' || typeof titleid !== 'string') {
    console.log('getLayerFromTitleID: Invalid titleid', titleid)
    return null
  }

  titleid = titleid.toLowerCase()

  const layerConfs: ConfigLayerOptions[] = Model.get('config.layers')
  for (const layerConf of layerConfs) {
    const titleidFromLayer = layerConf.titleid
    if (!titleidFromLayer) {
      console.log('Invalid or missing title for layer', layerConf)
      continue
    }
    if (titleidFromLayer === titleid) {
      return layerConf
    }
  }
  console.log('getLayerFromTitleID: Layer not found for titleid! ', titleid)
  return null
}

/**
 * Query the site config for a layer in a QGIS project.
 *
 * @param {string} titleid
 * @returns The layer configuration or null if not found
 *
 */
export const getLayerFromQgisDatasources = async (titleid: string) => {
  const layerConfs: ConfigLayerOptions[] = Model.get('config.layers')
  for (const layerConf of layerConfs) {
    if (layerConf.type.toLowerCase() !== 'qgisprojectsettings') {
      continue
    }

    const qgisLayerConf = await ConfigLayerQGISProjectSettings.fetchLayerByTitleId(layerConf as ConfigLayerCapabilityOptions, titleid)
    if (qgisLayerConf) {
      return qgisLayerConf
    }
  }
  console.debug('getLayerFromQgisDatasources: Layer not found in any QGIS project', titleid)
  return null
}

export const getLayerGroupID = (layerConf: ConfigLayerOptions) => {
  if (!layerConf) {
    console.warn('Invalid layerConf', layerConf)
    return
  }

  let groupId = layerConf.groupid || ''
  if (!groupId) {
    const groupName = getLayerGroupName(layerConf)
    if (groupName) {
      groupId = getPoziIDFromString(groupName)
    }
  }
  // console.debug('getLayerGroupID', groupId, layerConf)

  return groupId
}

export const getLayerGroupName = (layerConf: ConfigLayerOptions) => {
  if (!layerConf) {
    console.warn('Invalid layerConf', layerConf)
    return
  }

  const groupName = layerConf.group || ''
  // console.debug('getLayerGroupName', groupName, layerConf)

  return groupName
}

/**
 * Query the site config for a layer.
 *
 * https://lodash.com/docs/4.17.4#get
 * @param {string} titleid // As retrieved from getPoziIDFromString
 * @returns {object}
 *
 */
export const getReduxLayerFromTitleID = function (titleid: string) {
  if (isNil(titleid) || titleid === '') {
    console.log('getLayerFromRedux: Invalid titleid')
    return null
  }

  const state = reduxStore.getState()

  if (!state || !state.layers) {
    console.log('getLayerFromRedux: Invalid store state.')
    return null
  }

  for (const key in state.layers) {
    if (getPoziIDFromString(state.layers[key].title) === titleid) {
      return state.layers[key]
    }
  }
  console.error('getLayerFromRedux: Layer not found for titleid! ', titleid)
  return null
}

/**
 * Converts a layer name or title into a string that's suitable for use as a WFS typename.
 *
 * WFS doesn't like spaces in typenames, nor colons if they're not part of a namespace.
 * Since there is no direct link between a WMS layer name and its corresponding WFS name,
 * as a bit of a hack, this function replaces spaces with underscores and colons with hyphens.
 *
 * @param {string} layerNameOrTitle - the layer name or title to be converted
 * @returns {string} - the converted string
 *
 */
export const getWfsTypeNameFromTitle = (layerNameOrTitle: string) => layerNameOrTitle.replace(/ /g, '_').replace(/:/g, '-')

/**
 * Get URL from layer config object, taking into account potential URL params
 * that may be set in the layer config
 *
 */
export const getLayerUrl = (layerConf: ConfigLayerOptions, extraParams = {}, addProxyUrl = true) => {
  // Relevant config for WMS/Tile layers is in 'config' and for
  // vector layers in 'config.spatial'
  // TODO: one day have only one spot where the spatial config resides
  let url = layerConf?.config?.spatial?.url || layerConf?.config?.url

  // console.debug('getLayerUrl', layerConf?.titleid, extraParams, addProxyUrl, url)

  if (!url) {
    console.warn('getLayerUrl - URL not found in layerConf. Doing nothing.', layerConf)
    return null
  }

  // Add any params from config to the URL using the URL object
  const configParams = layerConf.config?.spatial?.params || layerConf.config?.params

  const params: Record<string, string> = {
    ...configParams,
    ...extraParams
  }

  if (params) {
    // Create object representation of the URL
    try {
      const baseUrl = window.location.href // use current page url if url is relative
      const urlObj = new URL(url, baseUrl)

      // Add params to URL object
      Object.entries(params).forEach(([key, value]) => {
        // Ignore any keys that have no value
        if (typeof value === 'undefined') {
          return
        }

        // Set/override URL params
        urlObj.searchParams.set(key, value)
      })

      // Retrieve URL as string from object
      url = urlObj.toString()
    } catch (error) {
      console.log('getLayerUrl - error creating URL object', error, url)

      PoziNotifications().warning(`Problem with URL for layer '${layerConf.title}': ${url}`)
      return null
    }
  }

  // Automatically add a proxy URL if defined in config and not disabled in calling this method
  const proxyUrl = addProxyUrl ? getLayerProxyUrl(layerConf) : undefined
  if (proxyUrl) {
    url = `${proxyUrl}${url}`
    // console.debug('getLayerUrl - proxyUrl detected. Adding! New URL:', url)
  }

  return url
}

export const getUrlParamsObj = (url: string) => {
  // console.debug('getUrlParamsObj', url)

  try {
    const urlObj = new URL(url)

    const paramsObj = Object.fromEntries([...urlObj.searchParams]) // create object from search params

    // console.debug('getUrlParamsObj - paramsObj', url, paramsObj)

    return paramsObj
  } catch (error) {
    console.log('getUrlParamsObj - error creating URL object', error, url)

    return null
  }
}
/**
 * Get Proxy headers from layer config object
 *
 * @param {import("config/ConfigLayer").ConfigLayerOptions} layerConf The layer config
 *
 * @returns {Record<string, string>} url
 *
 */
export const getLayerProxyHeaders = (layerConf: ConfigLayerOptions) => {
  const proxyHeaders = layerConf?.config?.spatial?.proxyHeaders || layerConf?.config?.proxyHeaders

  // console.debug('getLayerProxyHeaders', layerConf.titleid, proxyHeaders)

  return proxyHeaders
}

/**
 * Get Proxy URL from layer config object
 *
 * @param {import("config/ConfigLayer").ConfigLayerOptions} layerConf The layer config
 *
 * @returns {string} url
 *
 */
export const getLayerProxyUrl = (layerConf: ConfigLayerOptions) => {
  const proxyUrl = layerConf?.config?.spatial?.proxyUrl || layerConf?.config?.proxyUrl

  // console.debug('getLayerProxyUrl', layerConf.titleid, proxyUrl)

  return proxyUrl
}

/**
 * Get internal layer object from olLayer object.
 *
 * https://lodash.com/docs/4.17.4#get
 * @param {string} olLayer // As retrieved from getPoziIDFromString
 * @returns {object}
 *
 */
export const getLayerFromOLLayer = function (olLayer: OLLayerBase | OLLayer) {
  if (isNil(olLayer)) {
    return null
  }
  const titleid = olLayer.get('titleid') || ''
  return getLayerFromTitleID(titleid)
}

/**
 * Get all OpenLayers layers from the map or provided LayerGroup.
 * OpenLayers currently does not provide a way to get all layers,
 * including the layergroups. We need this functionality for our layertree
 *
 * @param {object} OLLayerGroup // Optional
 * @returns {array} Array of OpenLayers layers/layergroups
 *
 */
export const getAllOLLayersAndGroups = (olLayerGroup?: OLLayerGroup) => {
  const layers: OLLayer[] = []

  if (olLayerGroup && !olLayerGroup.getLayers) {
    console.warn('getAllOLLayersAndGroups - invalid layergroup provided', olLayerGroup)
    return []
  }

  const getChildLayersRecursively = (childLayerGroup: OLLayer[] | OLCollection<OLLayerBase | OLLayer>) => {
    // Handle case where provide argument is a non-group OL layer
    if (!(childLayerGroup.forEach)) {
      console.warn('getChildLayersRecursively - invalid child layergroup', childLayerGroup)
      return
    }

    childLayerGroup.forEach(layer => {
      layers.push(layer as OLLayer) // always add layer, even if it's a layergroup!
      if (layer instanceof OLLayerGroup) {
        getChildLayersRecursively(layer.getLayers())
      }
    })
  }

  const app = getApp()
  const parentGroup = olLayerGroup?.getLayers() || app?.map?.getLayers()
  if (parentGroup) {
    getChildLayersRecursively(parentGroup)
  }

  // console.debug('getAllOLLayersAndGroups', layers)

  return layers
}

/**
 * Get the OpenLayers layer object from the layer titleid
 * Note: this is quite inefficient and will slow down the app when there are many layers
 *
 */
export const getOpenLayersObjectFromTitleID = function (titleid: string) {
  // const olLayers = app.map.getLayers().getArray() // This does not get the child layers of layergroups
  // const olLayers = app.map.getAllLayers() // This does not get the layergroups
  const olLayers = getAllOLLayersAndGroups() || []

  // console.debug('getOpenLayersObjectFromTitleID', titleid, olLayers)

  for (const olLayer of olLayers) {
    const olLayerTitleId = olLayer.get('titleid')

    if (isNil(olLayerTitleId)) {
      // Invalid id
    } else if (olLayerTitleId === titleid) {
      return olLayer
    }
  }
  // console.debug('OpenLayers layer not found for titleid! ', titleid)

  return null
}

/**
 * Get the OpenLayers features from a given extent and return it in the PoziLayer format
 *
 * @returns {Array} Array of layers[[features],[...]]
 * onlyFeaturesFromLayer is a layer object that is optional... excludes any features not on that layer.
 *
 */
export const getOpenLayersFeaturesInExtent = function (onlyFeaturesFromLayer?: ConfigLayerOptions) {
  const app = getApp()
  const extent = app.map.getView().calculateExtent(app.map.getSize())
  if (isNil(extent)) {
    return []
  }
  const olLayers: OLLayer[] = app.map.getAllLayers()
  const layerArr: PoziStats[] = []
  for (const olLayer of olLayers) {
    if (!olLayer.getVisible()) {
      continue
    }
    const titleid = olLayer.get('titleid') || ''
    var layerConf = getLayerFromTitleID(titleid)

    // Skip feature if onlyFeaturesFromLayer is provided and doesn't match.
    if (!isNil(onlyFeaturesFromLayer)) {
      if (onlyFeaturesFromLayer.titleid !== layerConf?.titleid) {
        continue
      }
    }

    const source = olLayer.getSource()
    var count = 0
    var elementIndex = layerArr.length
    if (source instanceof OLSourceVector) {
      source.forEachFeatureInExtent(extent, (feature: OLFeature) => {
        if (isFunction(feature.getProperties)) {
          const props = feature.getProperties()
          const isVisible = get(props, 'visible', true)
          if (!isVisible) {
            return
          }
          if (isNil(layerArr[elementIndex])) {
            const newPoziStats = {
              config: layerConf,
              count: 0,
              features: []
            }
            elementIndex = layerArr.push(newPoziStats) - 1
          }
          count++

          const currLayerFeatures = layerArr[elementIndex].features
          if (currLayerFeatures instanceof Array) {
            currLayerFeatures.push(feature.getProperties())
          }
        }
      })
    }
    if (!isNil(layerArr[elementIndex])) {
      layerArr[elementIndex].count = count
    }
  }
  return layerArr
}

/**
 * Get the OpenLayers layer object from the layer titleid
 *
 * @returns {Array} Array of layers[[features],[...]]
 * onlyFeaturesFromLayer is a layer object that is optional... excludes any features not on that layer.
 *
 */
export const isFeatureInExtent = function (olFeature?: OLFeature, onlyFeaturesFromLayer?: ConfigLayerOptions) {
  const app = getApp()
  const extent = app.map.getView().calculateExtent(app.map.getSize())
  if (isNil(extent)) {
    return []
  }

  let bFeatureInExtents = false

  const olLayers = app.map.getAllLayers()
  for (const key in olLayers) {
    const olLayer = olLayers[key]
    if (!olLayer.getVisible()) {
      continue
    }
    const titleid = olLayer.get('titleid') || ''
    const layer = getLayerFromTitleID(titleid)
    if (!layer) {
      continue
    }

    // Skip feature if onlyFeaturesFromLayer is provided and doesn't match.
    if (!isNil(onlyFeaturesFromLayer)) {
      if (onlyFeaturesFromLayer.titleid !== layer.titleid) {
        continue
      }
    }

    const source = olLayer.getSource()
    if (isFunction(source.forEachFeatureInExtent)) {
      source.forEachFeatureInExtent(extent, (feature: OLFeature) => {
        if (bFeatureInExtents) return bFeatureInExtents
        if (olFeature === feature) {
          bFeatureInExtents = true
        }
      })
    }
  }

  return bFeatureInExtents
}

// onlyFeaturesFromLayer is a layer object that is optional... excludes any features not on that layer.
export const getFeaturesInExtentStats = function (onlyFeaturesFromLayer: ConfigLayerOptions) {
  const layerArrStats = getOpenLayersFeaturesInExtent(onlyFeaturesFromLayer) // Layers must have properties to be included
  const excludeArr = Model.get('config.layout.drawer.tabs.tools.print.stats.exclude', [])
  const stats = []

  for (let i = 0; i < layerArrStats.length; i++) {
    const layerStats = layerArrStats[i]
    const title = layerStats.config?.title
    if (includes(excludeArr, title)) {
      continue
    }
    stats.push(_extractPropertiesStats(layerStats))
  }
  return stats
}

const _extractPropertiesStats = function (layerStats: PoziStats) {
  const stats: PoziStats = {
    properties: [],
    title: get(layerStats, 'config.title', ''),
    titleid: get(layerStats, 'config.titleid', '')
  }

  const includeProperties = get(layerStats, 'config.print.stats.include.properties', [])
  const features = get(layerStats, 'features', [])

  let i
  for (i = 0; i < features.length; i++) {
    const feature = features[i]
    for (const featureKey in feature) {
      const value = feature[featureKey]
      if (isNumber(value)) {
        // Include all if no includes value is set. Else include properties in config.
        if (includeProperties.length === 0 || includes(includeProperties, featureKey)) {
          // console.log(featureKey + ' -> ' + value)
          if (!stats.properties) {
            continue
          }

          if (!Object.keys(stats.properties).includes(featureKey)) {
            stats.properties[featureKey] = {
              avg: 0,
              sum: 0
            }
          }
          stats.properties[featureKey as unknown as number].sum += value
        }
      }
    }
  }

  // Calculate average
  if (features.length > 0) {
    for (const propKey in stats.properties) {
      const prop = stats.properties[propKey]
      if (prop.sum !== 0) {
        prop.avg = Math.round((prop.sum / features.length) * 100) / 100
        prop.sum = Math.round(prop.sum * 100) / 100
      }
    }
  }

  stats.count = features.length

  return stats
}

/**
 * Query the layer for a filter.
 *
 * @param {object} layer // Layer
 * @param {string} filterid // As retrieved from getPoziIDFilter
 * @returns {object}
 *
 */
export const getLayerFilterFromFilterID = function (layer: ConfigLayerOptions, filterid: string) {
  if (!layer || !layer.titleid || !layer.filter || layer.filter.length === 0) {
    console.error('Unable to find filter on layer.', filterid)
    return null
  }
  for (const key in layer.filter) {
    const filter = layer.filter[key]
    if (getPoziIDFilter(layer, filter.name) === filterid) {
      return filter
    }
  }
  console.error('Filter not found for filterid.', filterid)
  return null
}

/**
 * Clean up a dirty object
 * A dirty object is one that may contain invalid characters.
 *
 * @param {Object | String} dirtyObjOrString
 * @returns {Object} cleanObj
 *
 */
export const cleanObjectOrString = function (dirtyObjOrString: Record<string, any> | string) {
  try {
    console.log('cleanObjectOrString dirtyObj', dirtyObjOrString)

    let dirtyObj = {}
    var cleanObj = {}

    if (typeof dirtyObjOrString === 'object' && dirtyObjOrString !== null) {
      dirtyObj = dirtyObjOrString
    } else {
      dirtyObj = JSON.parse(dirtyObjOrString)
    }

    cleanObj = JSON.parse(JSON.stringify(dirtyObj))
  } catch (e) {
    console.error('Model.cleanObject() Failed to clean object', e)
    return dirtyObjOrString // Attempt to continue by returning the original data.
  }

  return cleanObj
}

/**
 * Returns a string that can be safely used in JSON.
 * Strips any special characters
 * @returns JSON safe string
 */
export const getJSONSafeString = function (str: string) {
  if (isNil(str) || str === '') {
    // console.log('Invalid string.')
    return ''
  }
  const newStr = str.replace(/[^a-zA-Z0-9 ,;=#!?.[]-]+/g, '')
  return newStr
}

/**
 * Returns a string that can be safely used as an id.
 * Strips any special characters
 * @returns lowercase string containing only letters and numbers.
 */
export const getPoziIDFromString = function (title: string) {
  if (isNil(title) || !isString(title) || title === '') {
    console.log('getPoziIDFromString - empty or invalid title', title, typeof title)

    return ''
  }

  const id = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
    .trim()

  // console.debug('getPoziIDFromString', title, 'id', id)

  return id
}

/**
 * Returns a string that can be safely used as an filter id.
 * Strips any special characters
 * @returns lowercase string containing only letters and numbers.
 */
export const getPoziIDFilter = function (layer: ConfigLayerOptions, filtername: string) {
  if (isNil(layer) || !has(layer, 'titleid') || layer.titleid === '') {
    console.error('Invalid title value.')
    return null
  }

  if (isNil(filtername) || filtername === '') {
    console.error('Invalid filtername.', filtername)
    return null
  }

  const id = layer.titleid + '-' + filtername.toLowerCase().replace(/[^a-z0-9]+/g, '')
  return id
}

/**
 * Returns a string that can be safely used as an id.
 * Strips any special characters
 * @returns lowercase string containing only letters and numbers.
 */
export const getPoziTabIDFromConfigTitle = function (title: string) {
  if (isNil(title) || title === '') {
    console.error('Invalid title value.')
    return null
  }
  return 'tab-' + getPoziIDFromString(title)
}

// Determine if a attribute exists in a given layer
export const isAttributeInLayer = (layerConf: ConfigLayerOptions, attribute: string | null) => {
  const layerAttributes = layerConf?.attributes

  if (!layerAttributes || !attribute) return false

  return Object.keys(layerAttributes).some((key) => key.toLowerCase().includes(attribute.toLowerCase()))
}

// isLayerSelected
// If layer flag is set, or layer id is in url then return true
// Also determines if a basemap layer should be checked
export const isLayerSelected = function (layer: ConfigLayerOptions, bFirstLoad = false) {
  const { titleid } = layer

  const bLayerInStateModel = Model.valueInStateArray('layers', titleid)

  // Hack to mark a layer as selected as well as visible on startup
  // TODO: find a better spot to add this business logic
  if (bLayerInStateModel) {
    layer.selected = true
    layer.visible = true
  }

  // console.debug('isLayerSelected', titleid, bFirstLoad, bLayerInStateModel)

  if (bFirstLoad) {
    // Turn on if in state model (essentially a force on... ignore exclusive mode if in state model.)
    if (bLayerInStateModel) {
      return true
    }

    const bLayerInConfigVisible = get(layer, 'selectedAtLoad', false) // "visible": true - in layer config
    // console.debug('isLayerSelected - in config visible?', titleid, bLayerInConfigVisible)
    if (bLayerInConfigVisible) {
      // Special URL option to disable visible flag in layer config.
      const hideDefaultLayers = Model.getState('hidedefaultlayers', false)
      if (hideDefaultLayers) {
        console.log('isLayerSelected - default layer normally selected at load time', titleid)
        return false
      }

      // If not an exclusive group turn on (if set as visible in config)
      const groupid = get(layer, 'groupid', '')
      const bExclusiveGroup = IsExclusiveGroup(groupid)
      if (!bExclusiveGroup) {
        return bLayerInConfigVisible
      }

      // Exclusive group... check if another layer in this group is enabled.
      const stateLayers = Model.getStateArray('layers')

      if (!isArray(stateLayers)) {
        return true
      }

      let bSelectLayer = true
      stateLayers.forEach(function (layerSearchTitleId) {
        const layerSearch = getLayerFromTitleID(layerSearchTitleId)
        if (isNil(layerSearch)) {
          return
        }
        if (layerSearch.groupid === layer.groupid && layerSearch.titleid !== titleid) {
          // Found another layer in this exclusive group in the state model.
          bSelectLayer = false
        }
      })

      return bSelectLayer
    }
  }

  return bLayerInStateModel
}

/**
 * Determines if a layer is of type catalogue:
 *
 * - WFSCapabilities
 * - WMSCapabilities
 * - WMTSCapabilities
 * - QGISProjectSettings
 *
 * @param {object} layerConf // Layer config
 * @returns {bool}
 *
 */
export const isLayerTypeCatalogue = (layerConf?: ConfigLayerOptions) => {
  if (!layerConf) {
    console.log('isLayerTypeCatalogue - no layerConf provided', layerConf)
    return
  }

  const type = layerConf.type || ''
  const isCatalogue = !!type.match(/qgisprojectsettings|capabilities/i)
  // console.debug('isLayerTypeCatalogue', layerConf.titleid, type, isCapabilities)

  return isCatalogue
}

// Turn on an Openlayers layer
export const turnLayerOn = function (titleid: string) {
  return setMapLayerVisibility(titleid, true)
}

// Turn off an Openlayers layer
export const turnLayerOff = function (titleid: string) {
  return setMapLayerVisibility(titleid, false)
}

// https://openlayers.org/en/latest/apidoc/ol.source.html
// Options are the section config > params in the Pozi config json files.
export const olLayerSourceUpdateParams = function (titleid: string, params: Record<string, any>) {
  const olLayer = getOpenLayersObjectFromTitleID(titleid)
  if (olLayer) {
    const olSource = olLayer.getSource()
    if (olSource instanceof OLSourceImageWMS || olSource instanceof OLSourceTileWMS) {
      olSource.updateParams(params)
      console.log('Source params updated', titleid, params)
      return true
    }
  }
  console.error('Unable to update source params', titleid, params)
  return false
}

// Turn on/off an Openlayers layer. Called by turnLayerOn
// @returns provided visible value or undefined
export const setMapLayerVisibility = function (titleid: string, visible: boolean, trackLayerWhileOnInfoPanel = true) {
  console.debug('setMapLayerVisibility', titleid, visible)

  const olLayer = getOpenLayersObjectFromTitleID(titleid)

  if (!olLayer) {
    console.warn('setMapLayerVisibility - Unable to find layer:', titleid)
    return
  }

  const layerConf = getLayerFromTitleID(titleid)
  if (get(layerConf, 'config.transformer.pointOnSurface', false)) {
    console.log('setMapLayerVisibility - Do not turn on layers with pointOnSurface set to true. These require a lat/lng.')
    return
  }

  const olLayerIsVisible = olLayer.getVisible()

  // console.debug('setMapLayerVisibility - bSetVisible / bIsOlLayerVisible', titleid, bSetVisible, bIsOlLayerVisible)

  if (visible) {
    if (olLayerIsVisible) {
      console.debug('setMapLayerVisibility - Layer already on. Skipping turning on.', titleid)
      // Model.set('app.config.layerbytitleid[' + titleid + '].visible', true)
      // Model.set('app.event.layer.visible', titleid)
    } else {
      console.debug('setMapLayerVisibility - Turn layer on and update visible flag in model', titleid)

      olLayer.setVisible(visible) // Fires change:visible
      Model.set('app.config.layerbytitleid[' + titleid + '].visible', true)

      // Tracking a layer while on info panel will automatically turn a layer off when the info panel closes or resets
      // Do not track basemap(-like) layers as we do not want them to be switched off
      const groupid = layerConf?.groupid ?? ''
      const isBasemapLikeLayer = ['basemap', 'imagery', 'satellite', 'labels'].includes(groupid)
      if (trackLayerWhileOnInfoPanel && !isBasemapLikeLayer) {
        trackLayersTurnedOnWhileOnInfoPanel(titleid)
      }

      // Dispatch both the layer selected and visible actions
      window.store.dispatch(setLayerSelected(titleid)) // Note: this will cause setMapLayerVisibility to be called again
      window.store.dispatch(setLayerVisible(titleid))
    }
  } else {
    if (olLayerIsVisible) {
      console.debug('setMapLayerVisibility - turn layer off', titleid)
      olLayer.setVisible(visible) // Fires change:visible
      Model.set('app.config.layerbytitleid[' + titleid + '].visible', false)

      // Dispatch both the layer unselected and hidden actions.
      window.store.dispatch(setLayerUnselected(titleid))
      window.store.dispatch(setLayerHidden(titleid))
    } else {
      console.debug('Layer already off. Skipping turning off.', titleid)
      // Model.set('app.config.layerbytitleid[' + titleid + '].visible', false)
    }
  }

  return visible
}

export const trackLayersTurnedOnWhileOnInfoPanel = function (titleid: string) {
  const activetabid = Model.get('app.drawer.activetabid', null)

  // console.debug('trackLayersTurnedOnWhileOnInfoPanel', titleid, activetabid)

  // Only implements for layers for now.
  if (isNil(activetabid) || activetabid !== 'info') {
    return
  }

  console.debug('trackLayersTurnedOnWhileOnInfoPanel - tracking layer', titleid)

  Model.set('app.drawer.info.tracklayers[' + titleid + ']', titleid)
}

export const clearLayersTurnedOnWhileOnInfoPanel = function () {
  const titleIds = Model.get('app.drawer.info.tracklayers', [])

  console.debug('clearLayersTurnedOnWhileOnInfoPanel', titleIds)

  for (const i in titleIds) {
    const titleId = titleIds[i]
    turnLayerOff(titleId)
  }

  Model.unset('app.drawer.info.tracklayers')
}

// Turning parent linked layers off is currently disabled: this allows a user
// to turn off the linked layer (say, street names) to allow the parent layer
// (satellite image for example to remain visible)
export const turnParentLinkedLayersOff = function (titleid: string) {
  console.debug('turnParentLinkedLayersOff', titleid)
  const layerTurningOff = getLayerFromTitleID(titleid)
  const layerTitleTurningOffLower = layerTurningOff ? toLower(layerTurningOff.title) : ''

  const layers = Model.get('config.layers')
  if (!layers && layers.length > 0) return
  for (const layersIndex in layers) {
    const layer = layers[layersIndex]
    if (layer.title && layer.title !== '' && layer.linkedLayers && layer.linkedLayers.length > 0) {
      for (const linkedLayersIndex in layer.linkedLayers) {
        const linkedLayerTitle = layer.linkedLayers[linkedLayersIndex]
        if (linkedLayerTitle && linkedLayerTitle !== '') {
          const linkedLayerTitleLower = toLower(linkedLayerTitle)
          if (linkedLayerTitleLower === layerTitleTurningOffLower) {
            const linkedLayer = getLayerFromTitleID(layer.titleid)
            if (linkedLayer) {
              console.debug('turnParentLinkedLayersOff - turn off linked parent layer', linkedLayer.titleid)
              setMapLayerVisibility(linkedLayer.titleid, false)
            }
          }
        }
      }
    }
  }
}

export const getFeatureKey = function (feature: OLFeature, property: string) {
  if (!feature) {
    return null
  }
  const featVal = getFeaturePropertyValueCaseInsensitive(feature, property)
  if (featVal) {
    const featureKey = featVal.toLowerCase().trim()
    if (featureKey && featureKey !== '') {
      return featureKey
    }
  }
  console.log('Unable to find property', property, 'in feature', feature)
  return null
}

/**
 * @api {NA} PoziUtils.replaceAll Replace all
 * @apiDescription Replace all, including special characters. Use with care. Reference: https://stackoverflow.com/questions/1144783/how-to-replace-all-occurrences-of-a-string-in-javascript
 * @apiGroup Utils
 * @apiName replaceAll
 *
 * @apiParam {String} str Layer String to find/replace.
 * @apiParam {String} search Search string
 * @apiParam {String} replacement Replace string
 */
export const replaceAll = function (str: string, search: string, replacement: string) {
  let newStr = ''
  if (isString(str)) {
    newStr = str.split(search).join(replacement)
  }
  return newStr
}

export const getUrlParameter = function (sParam: string, url: string) {
  let sPageURL = window.location.search.substring(1)
  if (url) {
    sPageURL = url
  } else {
    console.error('DO NOT GET PARAMS FROM THE URL DIRECTLY!!! Use the model object. url.', sParam)
  }
  const sURLVariables = sPageURL.split('&')
  for (let i = 0; i < sURLVariables.length; i++) {
    const sParameterName = sURLVariables[i].split('=')
    if (sParameterName[0] === sParam) {
      return sParameterName[1]
    }
  }
  console.log('No value found in url for', sParam)
  return ''
}

export const isInAny = function (haystack: any, needle: any) {
  if (haystack && needle) {
    if (typeof haystack === 'string') {
      return haystack.indexOf(needle) > -1
    }
    if (typeof haystack === 'object') {
      let haystackBool = false
      $.each(haystack, function (key, value) {
        haystackBool = haystackBool || isInAny(value, needle) || false
      })
      return haystackBool
    }
  } else {
    return false
  }
}

export const findKeyInFeatureCollection = function (layerConf: ConfigLayerOptions, featureCollection: FeatureCollection) {
  let id = null
  if (layerConf && layerConf.config && layerConf.config.spatial && layerConf.config.spatial.id && layerConf.config.spatial.id !== '') {
    id = layerConf.config.spatial.id
    // If the id set is not found in the data then override it to be $id
    if (!isNil(featureCollection) && has(featureCollection, 'features') && featureCollection.features.length > 0) {
      const feature = featureCollection.features[0]
      const idPropName = findPropName(feature?.properties as Record<string, any>, id) // do case insensitive lookup
      if (!idPropName) {
        console.debug(`ID not found ('${id}') in properties. Using default`, layerConf.titleid, Model.DEFAULT_ID_WHEN_UNSET)
        id = Model.DEFAULT_ID_WHEN_UNSET
      } else {
        id = idPropName // set id to have the correct case
        // console.debug('ID found in properties', layerConf.titleid, id)
      }
    }
  } else if (featureCollection && featureCollection.features && featureCollection.features.length > 0 && featureCollection.features[0]) {
    id = GetPropertiesIdKey(layerConf, featureCollection.features[0].properties) // Attempt to find id from data.
    if (id) {
      console.log('ID was detected for layer', layerConf.title, 'ID detected from data:', id)
      return id
    } else {
      console.log('Unable to find an id for layer', layerConf.config, featureCollection.features[0].properties)
    }
  } else if (featureCollection && featureCollection.features && featureCollection.features.length === 0) {
    console.log('No features for layer', layerConf.config)
  } else {
    console.error('Unable to find or detect an id for layer', layerConf.config)
  }

  // console.debug('findKeyInFeatureCollection - id', layerConf.titleid, id)

  return id
}

export const paramsToUrl = function (paramsObj: Record<string, any>) {
  const urlParams = $.extend({}, paramsObj) // Need to clone the params using jQuery's extend?
  const str = []
  for (const p in urlParams) {
    if (!Object.prototype.hasOwnProperty.call(urlParams, p)) continue
    str.push(encodeURIComponent(p) + '=' + encodeURIComponent(urlParams[p]))
  }
  if (str.length === 0) {
    return ''
  }
  return str.join('&')
}

/**
 * sanitiseAddress
 *
 * - Remove australia, vic and postcode from end of string
 * - Make all text title case
 *   e.g.: '123 main_STREET_Natimuk, 3409, VIC, Australia'
 *   becomes: '123 Main Street Natimuk'
 *
 * @param {string} flatData
 *
 */
export const sanitiseAddress = (str: string) => {
  // console.debug('sanitiseAddress', str)

  let address = str

  // Remove any 'australia' at the end of the string '123 MAIN STREET NATIMUK, 3409, VIC, AUSTRALIA' )
  address = address.replace(/,?\s+australia$/ig, '')

  // Remove any 'vic' at the end of the string '123 MAIN STREET NATIMUK, 3409, VIC' )
  address = address.replace(/,?\s+vic$/ig, '')

  // Remove any postcodes at the end of the string (example: '123 MAIN STREET NATIMUK, 3409' )
  address = address.replace(/,?\s+\d{4}$/ig, '')

  // Convert lower/upper case words to TitleCase
  address = toTitleCase(address)

  return address
}

/**
 * toTitleCase
 *
 * Makes an address string nicer to read.
 *
 * Example: "123_main_street MINYIP" becomes "123 Main Street Minyip"
 *
 * @param {string} str
 *
 * @returns {string}
 */

export const toTitleCase = (str: string) => {
  return str.replace(/_/g, ' ').replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  })
}

/**
 * replaceUrlVariablesWithPropsValues
 *
 * Does a case insensitive match and replace of any variables in URL params against
 * the provided props object(s). If a value is a function, it will be called and the
 * return value will be used.
 *
 * Examples:
 *
 * - [$CQLgeometry] will be replaced by the value of props.$cglgeometry
 * - '[PROP_NUM]' will be replaced with the value(s) or props.PROP_NUM encapsulated
 *   in single quotes, e.g. ['100211', '100055', '100012']
 *
 * @param {string} url
 * @param {(object|object[])} propsObjArr
 *
 * @returns {string} url
 */
export const replaceUrlVariablesWithPropsValues = (url: string, propsObjArr: Record<string, any>[]) => {
  let urlObj
  try {
    urlObj = new URL(url)
  } catch (error) {
    console.warn('replaceUrlVariablesWithPropsValues - error creating URL object', error, typeof url, url)
    return url
  }

  // Convert the propsOjbArr into an array if it wasn't already
  if (!(propsObjArr instanceof Array)) {
    propsObjArr = [propsObjArr]
  }

  // console.debug('replaceUrlVariablesWithPropsValues', url, propsObjArr)

  // This function loops over the array of properties objects, finds any matching
  // values and then combines them, comma-separated and returns them.
  const replaceFn = (_dummy: any, match: string, key: string) => {
    // console.debug('replaceUrlVariablesWithPropsValues replaceFn', match, key)

    // If the match is wrapped in single quotes, wrap our resulting values
    const wrapValuesInSingleQuotes = match.match(/^'.+'$/)

    let propertyValues = []

    for (const propsObj of propsObjArr) {
      let propertyValue = getPropertyValueCaseInsensitive(propsObj, key)

      // Some computed properties can actually be functions, so we need to call those
      // in order to get the actual value
      if (typeof propertyValue === 'function') {
        propertyValue = propertyValue.call(propertyValue)
      }

      // Ignore missing values
      if (typeof propertyValue !== 'undefined') {
        propertyValues.push(propertyValue)
      }
    }

    // Wrapping in single quotes is needed for some queries
    if (wrapValuesInSingleQuotes) {
      propertyValues = propertyValues.map(propValue => `'${propValue}'`)
    }

    // Return the values comma-separated if there are multiple
    let returnValue = propertyValues.join(',')

    // If a match could not be found, return the string 'null', which is a valid value in most cases
    if (!returnValue) {
      returnValue = 'null'
    }

    // console.debug('replaceUrlVariablesWithPropsValues returnValue', returnValue)

    return returnValue
  }

  const variableMatchRegExp = /('?\[(\$?[_ \w]+?)\]'?)/g

  // For non-standard protocols that don't have a hostname, like "aul:event=runCommand&command=/f rtenq -f pcl_num [pcl_num]"
  if (!urlObj.host) {
    // console.debug('replaceUrlVariablesWithPropsValues urlObj:', urlObj)
    const newUrl = url.replace(variableMatchRegExp, replaceFn)
    return newUrl
  }

  // Loop through all URL search params, detect variables and replace them
  const searchParamsEntries = urlObj.searchParams.entries()
  for (const [key, value] of searchParamsEntries) {
    // console.debug(`replaceUrlVariablesWithPropsValues params: ${key}, ${value}`)

    // Replace any matches with a property value
    // Matches:
    // StringToMatch = 'foo bar \'[first]\' [second] "[third one]" blah blah'
    // * Match: '[first]' Key: '[first]' Key2 first
    // * Match: [second] Key: [second] Key2 second
    // * Match: [third one] Key: [third one] Key2 third one
    const newValue = value.replace(variableMatchRegExp, replaceFn)

    // Store changed value in URL object
    if (newValue !== value) {
      // console.debug(`replaceUrlVariablesWithPropsValues new value: ${key}, ${value} => ${newValue}`)

      urlObj.searchParams.set(key, newValue)
    }
  }

  // Replace any variables in the main URL (non-search params)
  // Decode the pathname first to restore spaces and other encoded characters
  // Eg. https://site.assetic.net/Assets/ComplexAsset/[Asset Identification]
  const decodedPathname = decodeURIComponent(urlObj.pathname)
  const newPathname = decodedPathname.replace(variableMatchRegExp, replaceFn)
  urlObj.pathname = newPathname

  // Return new URL
  return urlObj.toString()
}

export const sleep = (delay = 0) => new Promise((resolve) => {
  setTimeout(resolve, delay)
})

// Logic of toggling linked layers
export const linkedLayersToggle = (linkedLayers: string[], flag: boolean) => {
  const app = getApp()

  $.each(linkedLayers, function (k, v) {
    // Toggle visibility of all linked layers
    if (app.layerConfig && app.layerConfig[v] && app.layerConfig[v].config) {
      const layerConfig = app.layerConfig[v]
      if (!layerConfig) {
        return
      }
      const layerConf = app.layerConfig[v].config
      if (!layerConf) {
        return
      }
      const titleid = layerConf.titleid

      // console.debug('linkedLayersToggle', titleid)

      if (flag) {
        turnLayerOn(titleid)
      } else {
        turnLayerOff(titleid)
      }
    } else {
      // It's possible the layer is configured as linked layer, but actually not configured as an overlay (incorrect configuration though)
      console.log('linkedLayersToggle: Layer cannot be toggled, check configuration. Typically related to a deleted linked layer.', k, v)
    }
  })
}

/**
 * Checks if the given OpenLayers layer is within the current map resolution.
 *
 */
export const layerIsWithinResolution = (olLayer: OLLayer) => {
  const { maxResolution = Infinity, minResolution = 0 } = olLayer.getProperties()

  const map = getMap()

  if (!map) {
    return false
  }

  const mapResolution = map.getView().getResolution() || 0

  const layerWithinResolution = mapResolution >= minResolution && mapResolution < maxResolution

  // console.debug('layerWithinResolution', olLayer.get('titleid'), layerWithinResolution, minResolution, maxResolution, mapResolution)

  return layerWithinResolution
}

/**
 * Determines whether a layer shows a legend.
 *
 */
export const layerShowsLegend = (layerConf: ConfigLayerOptions) => {
  const isVisible = !!(layerConf.visible || layerConf.selected)
  const hasShowLegend = layerConf.showLegend ?? true
  const hasLegend = !!layerConf.legend || !isEmpty(layerConf.styleGeoStyler)
  const hasStyle = !isEmpty(layerConf.style) || !isEmpty(layerConf.styleClass)

  // const reduxLayerConf = getReduxLayerFromTitleID(layerConf.titleid)
  const reduxLayerConf = window.store.getState()
  const loadFailed = reduxLayerConf?.loadstatus === SET_LAYER_LOAD_FAILED

  let showsLegend = false

  // In case we have a nested layer tree, descend through it
  // Note: this is very inefficient and could slow down the application
  if (layerConf.layers) {
    showsLegend = layerConf.layers.some(layerShowsLegend)

    // console.debug('layerShowsLegend - nested layer', layerConf.titleid, showsLegend, layerConf)
  } else {
    showsLegend = isVisible && hasShowLegend && (hasLegend || hasStyle) && !loadFailed
  }

  // console.debug('layerShowsLegend', layerConf.titleid, hasShowLegend, hasLegend, showsLegend)

  return showsLegend
}

/**
 * Retrieves the visible layers that have a legend.
 *
 */
export const getVisibleLayersWithLegend = () => {
  // console.debug('getVisibleLayersWithLegend')

  // We will use this below to check if a layer has not failed loading
  const reduxLayerConfsObj = window.store.getState().layers

  const visibleLayersWithLegend = getAllOLLayersAndGroups() // start with all OL layers
    .filter(olLayer => olLayer.getVisible()) // only visible
    .filter(olLayer => layerIsWithinResolution(olLayer)) // only within resolution
    .map(olLayer => getLayerFromTitleID(olLayer.get('titleid'))) // convert to layerConfs
    .filter(layerConf => layerConf && reduxLayerConfsObj[layerConf.titleid]?.loadstatus !== SET_LAYER_LOAD_FAILED)
    .filter(layerConf => layerConf && layerShowsLegend(layerConf))

  // console.debug('getVisibleLayersWithLegend', visibleLayersWithLegend.map(layerConf => layerConf?.titleid), visibleLayersWithLegend)

  return visibleLayersWithLegend
}

// Note: this function is not currently in use
export const transformFeatureCollection = (featureCollection: FeatureCollection, to: ProjectionLike, from?: ProjectionLike) => {
  console.debug('transformFeatureCollection', featureCollection, to, from)

  const geojsonParser = new OLFormatGeoJSON()

  const olFeatures = geojsonParser.readFeatures(featureCollection)

  console.debug('transformFeatureCollection - olFeatures', olFeatures)

  const featureCollectionTransformed = geojsonParser.writeFeaturesObject(olFeatures, {
    dataProjection: to || 'EPSG:7844', // Like EPSG:4326 but GDA2020 style
    featureProjection: from || 'EPSG:3857'
  })

  console.debug('transformFeatureCollection - featureCollectionTransformed', featureCollectionTransformed)

  return featureCollectionTransformed
}
